<script lang="ts">
    import { onDestroy, onMount } from 'svelte'
    import { SvelteSet } from 'svelte/reactivity'
    import { derived as sderived, writable, type Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import { isEqual, orderBy } from 'lodash-es'
    import { toUnicode } from 'punycode-esm'
    import { filter } from 'rxjs/operators'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _, tgettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sgoto, spath } from 'hydria/svelte_helpers'
    import { getUnitCssVar } from 'hydria/utils/html'
    import { toLuxon } from 'hydria/utils/time'

    import { algorithmIndex, algorithms, ready as dnssecDataReady } from 'wdom/services/dnssec'
    import { stream } from 'wdom/services/stream'
    import type { StreamEvent } from 'wdom/services/stream'
    import { recordSortArray } from 'wdom/zone/zone'
    import type { DnssecKey, DsEntry, EditableRecord, NewEditableRecord, PubStatus, Record as RecordType, SaveError, Zone, ZoneDomainRelation, ZoneRevision, ZoneStatus, ZoneTemplate } from 'wdom/zone/zone'
    import { Breadcrumb } from 'breadcrumb'

    import Actions from './Actions.svelte'
    import Config from './Config.svelte'
    import Dnssec from './Dnssec.svelte'
    import GeneralWarnings from './GeneralWarnings.svelte'
    import RecordEdit from './RecordEdit.svelte'
    import RecordErrors from './RecordErrors.svelte'
    import Records from './Records.svelte'
    import RenewBlock from './RenewBlock.svelte'
    import Revisions from './Revisions.svelte'
    import SaveErrors from './SaveErrors.svelte'

    interface Props {
        idna: string
        mode: 'admin' | 'client'
        bottomColSave?: any
        addToCart?: (idna: string, cb?: () => void) => void
    }
    let { idna: zoneIdna, mode = 'client', bottomColSave, addToCart }: Props = $props()

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    const status: Writable<'loading' | 'ready' | 'not-found' | 'error'> = writable('loading')
    const busy: Writable<boolean | 'saving' | 'updating' | 'removing'> = writable(true)
    const zoneUnicode: Writable<string | undefined> = writable(undefined)

    $effect(() => {
        $zoneUnicode = zoneIdna != null ? toUnicode(zoneIdna) : undefined
        if (zoneIdna != null && $dnssecDataReady) loadZone()
    })

    const zone: Writable<Zone | undefined> = writable(undefined)
    const pubStatus: Writable<PubStatus> = writable({
        published: false,
        publishable: false,
        permanent: false,
        expiration: null,
    })
    const zoneStatus: Writable<ZoneStatus | null> = writable(null)
    const currentRevision: Writable<number | undefined> = writable(undefined)
    const zoneRevisions: Writable<ZoneRevision[]> = writable([])
    const zoneDomainRelation: Writable<ZoneDomainRelation> = writable()
    const recordErrors: Writable<{ [key: string]: string }> = writable({})
    const saveErrors: Writable<SaveError[]> = writable([])

    const crumbs = sderived(status, ($ready) => [
        { txt: $_('Zones'), url: spath('/zones') },
        {
            txt: $status == 'ready' ? $zoneUnicode : $status == 'loading' ? '…' : iconExclamationTriangle,
            html: $status != 'ready' && $status != 'loading',
        },
    ])

    let scrollY: number = $state(0)
    let showPublishNoSlots = $state(false)
    let addingZones = $state(false)
    let importWarnings: { key: string; message: string }[] = $state([])

    const records: Writable<EditableRecord[]> = writable([])

    // IMPORTANT: do not edit zoneEdit.records, do so in `records`.
    const zoneEdit: Writable<Zone | undefined> = writable(undefined)

    let hasRenewalAccess: boolean = $state(false)

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                     Server actions (load, save, etc)
    // ╰─────────────────────────────────────────────────────────────────────┄

    async function loadZone() {
        zone.set(undefined)
        status.set('loading')
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/zone/1/${zoneIdna}`)
        } catch {
            $status = 'not-found'
            return
        }
        importServerData(r.data)
        importDnssecData(r.data)
        hasRenewalAccess = r.data.has_renewal_access ? true : false
        $status = 'ready'
        $busy = false
        zoneModifiedOnBackend = false
    }

    async function loadRevision(revision: number) {
        busy.set('updating')
        if (!$zone) return

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/zone/1/${$zone.idna}/revision/${revision}`)
        } catch (e: any) {
            r = e
            notify({
                type: 'danger',
                title: $_('Load revision'),
                text: $_('An error occured while loading the revision information'),
            })
            console.error('Error loading revision:', r)
            return
        } finally {
            $busy = false
        }

        if (r && r.data && r.data.item) {
            currentRevision.set(r.data.item.revision)

            return loadZoneData(r.data.item)
        }
    }

    async function saveZone() {
        if ($busy) return

        const zEdit = $zoneEdit

        if (!zEdit || !$zone) return
        const data = {
            idna: zEdit.idna,
            data: {
                default_ttl: zEdit.default_ttl,
                minimum_ttl: zEdit.minimum_ttl,
                revision: zEdit.revision,
            },
            records: zEdit.records,
        }
        busy.set('saving')

        const serrors: SaveError[] = []

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', `/json/zone/1/${$zone.idna}`, data)
        } catch (e: any) {
            r = e

            if (e && e.data && e.data.records && e.data.records.length) {
                for (const erec of e.data.records) {
                    serrors.push({
                        type: erec.type,
                        // Stringify to expose invisible stuff like tabs, turning into \t (yes, it happened)
                        // record: JSON.stringify(rec.idna),
                        record: erec.repr_idna,
                        message: erec.error,
                    })
                }
            }
            notify({
                type: 'danger',
                title: $_('Save'),
                text: $_('Failed to validate the final zone text: {error}', { error: e && e.data && e.data.error ? e.data.error : '' }),
            })

            if (e && e.data && e.data.error_list != null && e.data.error_list.records != null) {
                for (const estr of e.data.error_list.records) {
                    const m = estr.match(/^\[(.+?), (.+?)\] (.*)$/)
                    if (m) {
                        serrors.push({
                            type: m[2],
                            record: m[1],
                            message: m[3],
                        })
                    } else {
                        serrors.push({ raw: estr })
                    }
                }
            }
        }

        if (r && r.data && r.data.success) {
            importServerData(r.data, true)
            importDnssecData(r.data)
            notify({
                type: 'success',
                title: $_('Save'),
                text: $_('The information has been saved successfully.'),
            })
        }

        saveErrors.set(serrors)
        busy.set(false)
    }

    async function setPublish(published: boolean) {
        if ($busy || !$zone || !isEqual($zone, $zoneEdit)) return

        showPublishNoSlots = false
        importWarnings = []
        busy.set('saving')
        let r: SimpleFetchResult
        try {
            r = await simpleFetch(published ? 'PUT' : 'DELETE', `/json/zone/1/${$zone.idna}/publish`)
        } catch (e: any) {
            r = e
        }
        busy.set(false)

        if (r && r.data) {
            if (r.data.success) {
                importServerData_status(r.data)
                importDnssecData(r.data)
            } else if (r.data.ecode === 'NO_SLOTS') {
                showPublishNoSlots = true
            } else {
                console.error('Unhandled exception in setPublish,', r)
            }
            if (r.data.zone_domain_relation != null) zoneDomainRelation.set(r.data.zone_domain_relation)
        } else {
            console.error('Unhandled exception in setPublish,', r)
        }
    }

    async function deleteZone(reason: string) {
        if ($busy || !$zone || !isEqual($zone, $zoneEdit)) return

        busy.set('removing')
        try {
            await simpleFetch('DELETE', `/json/zone/1/${$zone.idna}`, { reason: reason })
            notify({
                type: 'success',
                title: $_('Zone removal'),
                text: $_('The zone has been deleted.'),
            })
            sgoto('/zones')
        } catch (e: any) {
            console.error('Failed to delete zone:', e)
            if (e && e.data && e.data.ecode == 'PUBLISHABLE') {
                notify({
                    type: 'danger',
                    title: $_('Zone removal'),
                    text: $_('The zone file cannot be deleted because it’s still publishable. Try again once the zone has expired.'),
                })
            } else {
                notify({
                    type: 'danger',
                    title: $_('Zone removal'),
                    text: $_('The zone file could not be deleted.'),
                })
            }
            busy.set(false)
        }
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                               Load data from actions
    // ╰─────────────────────────────────────────────────────────────────────┄

    function importServerData_status(data: any) {
        if (data.zone_status && data.zone_status.published != null) {
            pubStatus.update((v) => {
                v.published = data.zone_status.published
                return v
            })
        }
        // pubStatus.set(data.status)
        for (const item of data.zone_status.dnssec_keys) {
            for (const key of ['ctime', 'expire_on', 'deleted_on']) {
                if (item[key] != null) item[key] = toLuxon(item[key])
            }
        }
        if (data.zone_status.dnssec_last_sync != null) data.zone_status.dnssec_last_sync = toLuxon(data.zone_status.dnssec_last_sync)
        zoneStatus.set(data.zone_status)
    }

    function importServerData(data: any, afterSave: boolean = false) {
        if (data.item.idna !== zoneIdna) {
            console.error(`Weird situation: we are in the zone “${zoneIdna}”, but we received data for “${data.item.idna}”; changing URL.`)
            status.set('loading')
            sgoto(`/zone/${data.item.idna}`)
            return
        }

        for (const key of ['ctime', 'mtime']) {
            data.item[key] = toLuxon(data.item[key])
        }

        // Simplifies checks when comparing with $zoneEdit.records
        // to detect changes
        data.item.records = orderBy(data.item.records, [(rec) => recordSortArray(zoneIdna, rec)])

        zone.set(data.item)

        if (data.status.expiration != null) data.status.expiration = toLuxon(data.status.expiration)
        pubStatus.set(data.status)

        importServerData_status(data)

        if (afterSave) {
            zoneRevisions.update((revs) => {
                revs.unshift({
                    action: 'update',
                    stamp: data.item.mtime,
                    revision: data.item.revision,
                })
                return revs
            })
        } else {
            for (const item of data.revisions) {
                item.stamp = toLuxon(item.stamp)
            }
            zoneRevisions.set(data.revisions)
        }
        currentRevision.set(data.item.revision)

        let eds: EditableRecord[] = []
        for (const record of data.item.records) {
            eds.push({
                saved: record,
                edited: { ...record },
                status: 'saved',
                hasError: false,
            })
        }
        eds = orderBy(eds, [(ed) => recordSortArray(zoneIdna, ed)], ['asc'])

        records.set(eds)

        const zedit = { ...data.item }
        zedit.records = [...data.item.records]
        zoneEdit.set(zedit)

        zoneDomainRelation.set(data.zone_domain_relation)
    }

    function loadZoneData(item: { [key: string]: any }) {
        const ze = $zoneEdit
        const revIndex: {
            [key: string]: {
                [key: string]: RecordType[]
            }
        } = {}

        for (const record of item.records) {
            if (revIndex[record.type] == null) {
                revIndex[record.type] = {}
            }
            if (revIndex[record.type][record.idna] == null) {
                revIndex[record.type][record.idna] = []
            }
            revIndex[record.type][record.idna].push(record)
        }

        // Processed $record entries. Finally, this will be the new $records
        let newRecords: EditableRecord[] = []

        // Records in memory, with changes reverted
        let origRecords = $records
            .filter((rg) => rg.status != 'new')
            .map((rg) => {
                if (rg.status == 'changed' || rg.status == 'remove') {
                    rg.edited = { ...rg.saved }
                    rg.status = 'saved'
                }
                return rg
            })

        // First pass: find those with no match or perfect matches.
        // If the record type + idna doesn’t exist, then it’s deleted.
        // If the record type + idna exists, and the other data is equal, perfect match.
        for (const rg of origRecords) {
            if (revIndex[rg.saved.type] == null || revIndex[rg.saved.type][rg.saved.idna] == null) {
                rg.status = 'remove'
                rg.edited = { ...rg.saved }
                rg.hasError = false
                newRecords.push(rg)
                continue
            }

            let perfectMatch: number | undefined
            for (const [i, rr] of revIndex[rg.saved.type][rg.saved.idna].entries()) {
                if (isEqual(rr, rg.saved)) {
                    perfectMatch = i
                    break
                }
            }
            if (perfectMatch !== undefined) {
                // rg.status = 'saved'
                // rg.edited = { ...rg.saved }
                // rg.hasError = false
                revIndex[rg.saved.type][rg.saved.idna].splice(perfectMatch, 1)
                newRecords.push(rg)
                if (revIndex[rg.saved.type][rg.saved.idna].length == 0) {
                    delete revIndex[rg.saved.type][rg.saved.idna]
                }
            }
        }

        // Second pass: all that remains are not exact matches
        for (const rg of origRecords) {
            if (newRecords.includes(rg)) {
                // already processed above
                continue
            }

            const firstMatch = revIndex[rg.saved.type][rg.saved.idna].shift()
            if (firstMatch === undefined) {
                rg.status = 'remove'
                rg.edited = { ...rg.saved }
                rg.hasError = false
                newRecords.push(rg)
                if (revIndex[rg.saved.type][rg.saved.idna].length == 0) {
                    delete revIndex[rg.saved.type][rg.saved.idna]
                }
            } else {
                rg.edited = { ...firstMatch }
                rg.status = isEqual(rg.saved, rg.edited) ? 'saved' : 'changed'
                if (rg.status !== 'changed') {
                    console.warn('Unexpected situation: saved === edited, but should not be', rg)
                }
            }

            newRecords.push(rg)
        }

        // And lastly.. add all the unprocessed entries
        for (const typeEntries of Object.values(revIndex)) {
            for (const entries of Object.values(typeEntries)) {
                for (const record of entries) {
                    newRecords.push({
                        saved: record,
                        edited: { ...record },
                        status: 'new',
                        hasError: false,
                    })
                }
            }
        }

        newRecords = orderBy(newRecords, [(ed) => recordSortArray(item.idna, ed)], ['asc'])

        if (ze) {
            ze.default_ttl = item.default_ttl
            ze.minimum_ttl = item.minimum_ttl
            // ze.records = [...item.records]
        }
        zoneEdit.set(ze)

        // This should trigger the update of zoneEdit.records
        records.set(newRecords)
    }

    function loadTemplateData(template: ZoneTemplate) {
        const rgIndex: {
            [key: string]: {
                [key: string]: {
                    cleaned: boolean
                    records: EditableRecord[]
                }
            }
        } = {}

        const rgUsed = []
        let lrecords = $records
        for (const rg of lrecords) {
            if (rgIndex[rg.saved.type] == null) rgIndex[rg.saved.type] = {}
            if (rgIndex[rg.saved.type][rg.saved.idna] == null) rgIndex[rg.saved.type][rg.saved.idna] = { cleaned: false, records: [] }
            rgIndex[rg.saved.type][rg.saved.idna].records.push(rg)
        }

        for (const trec of template.records) {
            const index = rgIndex[trec.type] && rgIndex[trec.type][trec.idna] ? rgIndex[trec.type][trec.idna] : null
            if (index && !index.cleaned) {
                for (const rg of index.records) rg.status = 'remove'
                index.cleaned = true
            }
            let rg: EditableRecord | undefined
            if (index && index.records.length) {
                for (const [i, lrg] of index.records.entries()) {
                    if (isEqual(lrg.saved, trec)) {
                        index.records.splice(i, 1)
                        rg = lrg
                        break
                    }
                }
            }
            if (!rg && index && index.records.length) {
                rg = index.records.shift()
            }
            if (rg) {
                rg.edited = { ...trec }
                rg.status = isEqual(trec, rg.saved) ? 'saved' : 'changed'
            } else {
                lrecords.push({
                    saved: { ...trec },
                    edited: { ...trec },
                    status: 'new',
                    hasError: false,
                })
            }
        }

        if ($zone) {
            records.set(orderBy(lrecords, [(v) => recordSortArray($zone.idna, v)]))
        }
    }

    function loadSaved() {
        // Reload all "saved" data
        const z = $zone
        const ze = $zoneEdit

        if (!z || !ze) return

        ze.default_ttl = z.default_ttl
        ze.minimum_ttl = z.minimum_ttl
        zoneEdit.set(ze)

        records.update((recs) => {
            return orderBy(
                recs
                    .filter((rec) => rec.status != 'new')
                    .map((rec) => {
                        if (rec.status != 'saved') {
                            rec.edited = { ...rec.saved }
                            rec.status = 'saved'
                        }
                        return rec
                    }),
                [(rec) => recordSortArray(zoneIdna, rec)],
                ['asc'],
            )
        })
        currentRevision.set($zone.revision)
    }

    function importZoneData(data: any, warnings: any[]) {
        importWarnings = warnings

        loadZoneData(data.item)
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                        Stream Events
    // ╰─────────────────────────────────────────────────────────────────────┄

    let zoneModifiedOnBackend = $state(false)
    let zoneDeletedOnBackend = $state(false)

    unsubs.push(
        stream.pipe(filter((e) => (e as StreamEvent).event == 'zone' && (e as StreamEvent).data.idna == zoneIdna)).subscribe({
            next: (e) => {
                if ($busy) {
                    console.warn('Zone event while busy, ignoring')
                    return
                }
                if ((e as StreamEvent).data.status == 'delete') {
                    zoneDeletedOnBackend = true
                    zoneModifiedOnBackend = true
                } else if ((e as StreamEvent).data.status == 'save') {
                    if ($zone && (e as StreamEvent).data.revision > $zone.revision) {
                        zoneModifiedOnBackend = true
                    }
                } else {
                    console.warn('Unhandled zone event:', (e as StreamEvent).data)
                }
            },
        }),
    )

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                               DNSSEC
    // ╰─────────────────────────────────────────────────────────────────────┄

    const allowedAlgorithms: Set<number> = new SvelteSet()
    const dnssecKeys: Writable<{ zsk: DnssecKey[]; ksk: DnssecKey[] }> = writable({ zsk: [], ksk: [] })
    const dnssecActiveKeyCount: Writable<{ zsk: number; ksk: number }> = writable({ zsk: 0, ksk: 0 })
    const rolloverAlgorithms: Writable<{ zsk: number[]; ksk: number[] }> = writable({ zsk: [], ksk: [] })
    const dsRecords: Writable<{ ds: DsEntry; key: DnssecKey }[]> = writable([])
    const dsRecordsHasExpiring: Writable<boolean> = writable(false)
    const dsRecordsHasActive: Writable<boolean> = writable(false)

    function importDnssecData(data: any) {
        // IMPORTANT:  for now, importServerData_status has to be called before this
        // (or importServerData which calls .._status).
        // It converts the times on the keys into luxon objects.
        allowedAlgorithms.clear()
        for (const alg of $algorithms) {
            if (!alg.allowed) continue
            if ($zoneDomainRelation.domain_exists && !alg[$zoneDomainRelation.domain_system]) continue

            allowedAlgorithms.add(alg.id)
        }

        const ldnssecKeys = {
            zsk: [] as DnssecKey[],
            ksk: [] as DnssecKey[],
        }
        const ldnssecActiveKeyCount = {
            zsk: 0,
            ksk: 0,
        }
        const lrolloverAlgorithms = {
            zsk: [] as number[],
            ksk: [] as number[],
        }

        const kskIndex = {}

        const sortedKeys: DnssecKey[] = orderBy(data.zone_status.dnssec_keys, [
            (key) => {
                switch (key._status) {
                    case 'active':
                        return `0:${key.ctime.toFormat('yyyyMMddHHmmss')}`
                    case 'expiring':
                        return `1:${key.expire_on.toFormat('yyyyMMddHHmmss')}`
                    case 'expired':
                        return `2:${key.expire_on.toFormat('yyyyMMddHHmmss')}`
                    case 'deleted':
                        return `3:${key.deleted_on.toFormat('yyyyMMddHHmmss')}`
                    default:
                        return '0'
                }
            },
            (key) => key.alg,
            (key) => key.key_tag,
        ])

        for (const key of sortedKeys) {
            ldnssecKeys[key.type].push(key)
            if (key.type == 'ksk') {
                kskIndex[`${key.alg}:${key.key_tag}`] = key
            }

            if (key._status == 'active') {
                ldnssecActiveKeyCount[key.type]++
            }

            const alg = $algorithmIndex[key.alg]
            if (allowedAlgorithms.has(alg.id)) lrolloverAlgorithms[key.type].push(key.alg)
        }

        // dsRecords = orderBy(data.zone_status.ds_entries, [(ds) => ds.key_tag, (ds) => ds.digest_type]).map((o) => {
        //     return { ds: o, key: null }
        // })
        const ldsRecords: { ds: DsEntry; key: DnssecKey | null }[] = orderBy(data.zone_status.ds_entries, [
            (ds) => {
                ds = ds as DsEntry
                switch (ds._status) {
                    case 'active':
                        return `0:${ds.ctime.toFoxmat('yyyyMMddHHmmss')}`
                    case 'expiring':
                        return `1:${ds.expire_on.toFoxmat('yyyyMMddHHmmss')}`
                    case 'expired':
                        return `2:${ds.expire_on.toFoxmat('yyyyMMddHHmmss')}`
                    case 'deleted':
                        return `3:${ds.deleted_on.toFoxmat('yyyyMMddHHmmss')}`
                    default:
                        return '0'
                }
            },
            (ds) => ds.alg,
            (ds) => ds.digest_type,
            (ds) => ds.key_tag,
        ]).map((o) => {
            return { ds: o, key: null }
        })

        dsRecordsHasExpiring.set(false)
        dsRecordsHasActive.set(false)

        for (const ds of ldsRecords) {
            const hash = `${ds.ds.alg}:${ds.ds.key_tag}`
            ds.key = kskIndex[hash]
            // if (ds.key._status == 'active' || ds.key._status == 'expiring') kskIndex[hash].dsRecords.push(ds)
            if (ds.key && ds.key._status == 'expiring') dsRecordsHasExpiring.set(true)
            if (ds.key && ds.key._status == 'active') dsRecordsHasActive.set(true)
        }

        // Should not happen, but just in case
        const finalLdsRecords = ldsRecords.filter((i) => {
            return i.key != null
        }) as { ds: DsEntry; key: DnssecKey }[]

        dnssecKeys.set(ldnssecKeys)
        dnssecActiveKeyCount.set(ldnssecActiveKeyCount)
        rolloverAlgorithms.set(lrolloverAlgorithms)
        dsRecords.set(finalLdsRecords)
    }

    async function generateNewKey(keyType: 'zsk' | 'ksk', algId: number) {
        if (!!$busy || !$zone) return

        let r: any

        busy.set('saving')
        try {
            r = await simpleFetch('POST', `/json/zone/1/${$zone.idna}/dnssec-key/${keyType}/${algId}`)
        } catch (e) {
            console.error('Failed to generate key:', e)
        }
        if (r) {
            importServerData_status(r.data)
            importDnssecData(r.data)
        }
        busy.set(false)
    }

    async function expireKey(key: DnssecKey) {
        if (!!$busy || !$zone) return

        let r: any

        busy.set('saving')
        try {
            r = await simpleFetch('DELETE', `/json/zone/1/${$zone.idna}/dnssec-key/${key.type}/${key.key_tag}/${key.alg}`)
        } catch (e) {
            console.error('Failed to remove key:', e)
        }
        if (r) {
            importServerData_status(r.data)
            importDnssecData(r.data)
        }
        busy.set(false)
    }

    async function unexpireKey(key: DnssecKey) {
        if (!!$busy || !$zone) return

        let r: any

        busy.set('saving')
        try {
            r = await simpleFetch('PUT', `/json/zone/1/${$zone.idna}/dnssec-key/${key.type}/${key.key_tag}/${key.alg}`)
        } catch (e: any) {
            console.error('Failed to restore key:', e)
            let err: string
            if (e.data && e.data.ecode == 'NO_MATCHING_ALT_TYPE') {
                err = $_('Restoring this key would result in a missing {type} key', { type: key.type == 'ksk' ? 'zsk' : 'ksk' })
            } else if (e.status == 404) {
                err = $_('The requested key could not be found.')
            } else {
                err = $_('Unhandled error')
            }
            notify({
                type: 'danger',
                title: $_('Key restoration'),
                text: err,
            })
            return
        } finally {
            busy.set(false)
        }
        importServerData_status(r.data)
        importDnssecData(r.data)

        if (r.data.n_unexpired_keys == 1) {
            notify({
                type: 'success',
                title: $_('Key restoration'),
                text: $_('The requested key was successfully restored'),
            })
        } else {
            notify({
                type: 'success',
                title: $_('Key restoration'),
                text: $_('The requested key was successfully restored, along a matching {type} key', { type: key.type == 'ksk' ? 'zsk' : 'ksk' }),
            })
        }
    }

    async function keyRollover(keyType: 'ksk' | 'zsk') {
        if (!!$busy || !$zone) return

        let r: SimpleFetchResult

        busy.set('saving')
        try {
            r = await simpleFetch('POST', `/json/zone/1/${$zone.idna}/dnssec-key/${keyType}/rollover`)
        } catch (e: any) {
            console.error('Failed to initiate key rollover:', e)
            r = e
        }
        if (r && r.data && r.data.success) {
            importServerData_status(r.data)
            importDnssecData(r.data)
        }
        busy.set(false)
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                         Add a record
    // ╰─────────────────────────────────────────────────────────────────────┄

    let addRecordGroup: NewEditableRecord | undefined = $state(undefined)

    function closeAddRecord() {
        if (!$zone || addRecordGroup == null) return

        if (addRecordGroup.edited.type != '') {
            records.update((v) => {
                v.unshift(addRecordGroup as EditableRecord)

                const idna = $zone.idna
                return orderBy(
                    v,
                    [
                        (ed) => {
                            return recordSortArray(idna, ed)
                        },
                    ],
                    ['asc'],
                )
            })
        }
        addRecordGroup = undefined
    }

    // let editRecordGroup: EditableRecord | undefined = $state(undefined)
    let editRecordPosition: number | undefined = $state(undefined)

    function closeEditRecord() {
        if (!$zone || editRecordPosition == null) return

        records.update((v) => {
            // v.unshift(addRecordGroup as EditableRecord)

            const idna = $zone.idna
            return orderBy(
                v,
                [
                    (ed) => {
                        return recordSortArray(idna, ed)
                    },
                ],
                ['asc'],
            )
        })
        editRecordPosition = undefined
    }

    // Re-sort the records in case something changed (like 'idna'),
    // and cause a propagation on the store.
    function recordsChanged() {
        if (!$zone) return

        records.update((v) => {
            const idna = $zone.idna
            return orderBy(
                v,
                [
                    (ed) => {
                        return recordSortArray(idna, ed)
                    },
                ],
                ['asc'],
            )
        })
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                     Cart/slots stuff
    // ╰─────────────────────────────────────────────────────────────────────┄

    async function addZoneToCart() {
        if (!addToCart) return
        if (addingZones || !$zone) return

        addingZones = true
        addToCart($zone.idna, () => {
            addingZones = false
        })
    }

    // ╭─────────────────────────────────────────────────────────────────────┄
    // │                                                 Helpers / Validation
    // ╰─────────────────────────────────────────────────────────────────────┄

    let recToZoneEditCount = 0
    unsubs.push(
        records.subscribe((recs) => {
            if ($status == 'ready' && recToZoneEditCount == 0) {
                const zedit = $zoneEdit
                if (zedit) {
                    recToZoneEditCount++
                    zedit.records = $records.filter((r) => r.status != 'remove').map((r) => r.edited)

                    simpleZoneValidation(zedit)

                    zoneEdit.set(zedit)

                    recToZoneEditCount--
                }
            }
        }),
    )

    function simpleZoneValidation(zoneEdit_: Zone) {
        // Perform a very simple zone validation. For now:
        //  TXT: max 255 bytes (encoded in UTF-8, so we should encode to UTF-8 too)
        //  CNAME: can only have one CNAME per name
        //  DNAME: can only have one DNAME per name
        //  CNAME + DNAME: cannot be on same name
        //  CNAME: cannot have a non-cname on same name
        const rErrors = {}
        const names = {
            // There’s always "SOA" on ''
            '': { CNAME: 0, DNAME: 0, other: 1 },
        }

        for (const record of zoneEdit_.records) {
            if (!names[record.idna]) {
                names[record.idna] = { CNAME: 0, DNAME: 0, other: 0 }
            }
            if (names[record.idna][record.type] == null) names[record.idna].other++
            else names[record.idna][record.type]++
        }

        for (const [idna, counts] of Object.entries(names)) {
            if (counts.CNAME && counts.DNAME) {
                rErrors[idna] = tgettext('CNAME and DNAME cannot be used together')
            } else if (counts.CNAME && counts.other) {
                rErrors[idna] = tgettext('CNAME cannot be mixed with other types')
            } else if (counts.CNAME > 1) {
                rErrors[idna] = tgettext('There can only be one CNAME')
            } else if (counts.DNAME > 1) {
                rErrors[idna] = tgettext('There can only be one DNAME')
            }
        }

        const idnaInError = Object.keys(rErrors)
        for (const record of $records) {
            record.hasError = idnaInError.includes(record.edited.idna)
        }

        recordErrors.set(rErrors)
        // records.update((v) => v)
    }

    let scrollSwitchPx: number = $state(0)
    let colSaveEl: HTMLElement | undefined = $state(undefined)
    onMount(() => {
        if (colSaveEl) {
            let realPos = colSaveEl.getBoundingClientRect().top + window.scrollY
            let th = getUnitCssVar('--hy-topbar-height')
            let gap = getUnitCssVar('--hy-generic-gap')
            if (th != null && gap != null) {
                scrollSwitchPx = realPos - th - gap
            } else {
                scrollSwitchPx = 80
            }
        }
    })
</script>

<style>
    #container-zone {
        --zone-sidebar-width: 16.5rem;
    }
    .col-content {
        /* margin-right: 280px; */
        margin-right: calc(var(--zone-sidebar-width) + 30px);
    }
    .ui-scrollfix.col-save {
        position: fixed;
        right: 30px;
        /* top: calc(var(--hy-topbar-height) + var(--hy-generic-gap)); */
        top: var(--hy-topbar-fixclear);
        bottom: 10px;
        overflow: auto;

        /* height: calc(100vh - 80px - 2rem); */
        height: auto;
        bottom: 30px;
    }
    .col-save {
        position: absolute;
        /* Fix le modal à l’intérieur de .col-save, moins de trouble que de portaller */
        z-index: 2;
        top: 0;
        right: 0;
        width: var(--zone-sidebar-width);

        display: flex;
        flex-direction: column;
        gap: 1.5rem;

        height: calc(100vh - 80px - 2rem - 65px);
    }

    @media (max-width: 767px) {
        .col-content {
            margin-right: 0;
        }
        .ui-scrollfix.col-save {
            position: relative;
            width: 100%;
            top: 0;
            right: 0;
        }
        .col-save {
            position: relative;
            width: 100%;
            top: 0;
        }
    }
</style>

<Breadcrumb {crumbs} />

<svelte:window bind:scrollY />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else}
    <div id="container-zone" class="position-relative status-{$status}">
        <div class="col-content">
            <div class="container-title">
                <div class="title-domain">
                    <div class="tagline">{$_('Votre zone')}</div>
                    <h1>
                        {#if $status == 'loading'}
                            <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                        {:else}
                            {$zoneUnicode}
                        {/if}
                    </h1>
                </div>
                {#if hasRenewalAccess && $pubStatus.publishable && !$pubStatus.permanent && $zoneUnicode && $pubStatus.expiration}
                    <div class="renew-block"><RenewBlock {zoneIdna} zoneUnicode={$zoneUnicode} expiration={$pubStatus.expiration} /></div>
                {/if}
            </div>

            <Config {status} {busy} {zone} {zoneEdit} class="mb-4" />

            {#if importWarnings.length}
                <div>
                    <div class="standalone-title text-warning">{$_('Importation warnings')}</div>
                    <div class="card mb-4">
                        <div class="card-body">
                            <dl class="mb-0">
                                {#each importWarnings as error}
                                    <dt>{error.key}</dt>
                                    <dd class="ns-3">{error.message}</dd>
                                {/each}
                            </dl>
                        </div>
                    </div>
                </div>
            {/if}

            {#if showPublishNoSlots}
                <div>
                    <div class="standalone-title text-warning">{$_('Cannot publish zone')}</div>
                    <div class="card mb-4">
                        <div class="card-body">
                            <p>{$_('This zone is not publishable. It can be published once you have purchased this option.')}</p>
                            {#if mode == 'client'}
                                <p>
                                    <button class="btn btn-outline-secondary" type="button" disabled={addingZones} onclick={() => addZoneToCart()}>{$_('Buy a year')}</button>
                                </p>
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}

            <GeneralWarnings {status} {pubStatus} {zoneDomainRelation} {dsRecordsHasActive} class="mb-4" />
            <SaveErrors {status} {saveErrors} class="mb-4" />

            <RecordErrors {status} {zoneUnicode} {recordErrors} class="mb-4" />
            <Records
                {zone}
                {status}
                {records}
                {busy}
                {zoneUnicode}
                {zoneEdit}
                {recordsChanged}
                class="mb-4"
                editRecord={(recPos: number) => {
                    editRecordPosition = recPos
                }}
            />

            <Dnssec {status} {zone} {busy} {zoneEdit} {zoneStatus} {zoneDomainRelation} {pubStatus} {dnssecKeys} {rolloverAlgorithms} {dnssecActiveKeyCount} {dsRecordsHasActive} {dsRecords} {allowedAlgorithms} {generateNewKey} {expireKey} {unexpireKey} rotateKey={(type) => keyRollover(type)} class="mb-4" />
        </div>

        <div class="col-save" bind:this={colSaveEl} class:ui-scrollfix={scrollSwitchPx != null && scrollY >= scrollSwitchPx}>
            <Actions
                {status}
                {zoneStatus}
                {zone}
                {busy}
                {mode}
                {zoneEdit}
                {zoneDomainRelation}
                {recordErrors}
                addRecord={() => {
                    addRecordGroup = {
                        saved: { idna: '', type: '', ttl: null },
                        edited: { idna: '', type: '', ttl: null },
                        status: 'new',
                        hasError: false,
                    }
                }}
                applyTemplateData={(data) => loadTemplateData(data)}
                {saveZone}
                publishZone={() => setPublish(true)}
                unpublishZone={() => setPublish(false)}
                {deleteZone}
                {importZoneData}
            />
            <Revisions {loadRevision} {loadSaved} {status} {busy} {zone} {currentRevision} {zoneRevisions} />

            {#if bottomColSave}{@render bottomColSave()}{/if}
        </div>
    </div>
    {#if addRecordGroup != null}
        <RecordEdit bind:rg={addRecordGroup} close={() => closeAddRecord()} {status} {zoneUnicode} {zone} />
    {:else if editRecordPosition != null}
        <RecordEdit bind:rg={$records[editRecordPosition]} close={() => closeEditRecord()} {status} {zoneUnicode} {zone} />
    {/if}
{/if}

{#if zoneModifiedOnBackend}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{zoneDeletedOnBackend ? $_('The zone has been deleted') : $_('The zone has been modified')}</div></div>

                <div class="modal-body d-grid gap-3">
                    {#if zoneDeletedOnBackend}
                        <p>{$_('Cette zone a été effacée sur le serveur.')}</p>
                    {:else}
                        <p>{$_('Cette zone a été modifiée sur le serveur.')}</p>
                    {/if}
                </div>

                <div class="modal-footer">
                    {#if $status == 'loading'}
                        <button class="btn btn-info" type="button" disabled aria-hidden="true"><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></button>
                    {:else}
                        <button
                            class="btn btn-outline-secondary"
                            type="button"
                            onclick={() => {
                                sgoto('/zones')
                            }}>{$_('Revenir à la liste de zones')}</button
                        >
                        {#if !zoneDeletedOnBackend}
                            <button
                                class="btn btn-primary"
                                type="button"
                                onclick={() => {
                                    loadZone()
                                }}>{$_('Recharger la zone')}</button
                            >
                        {/if}
                    {/if}
                </div>
            </div>
        </div>
        <div class="modal-backdrop" aria-hidden="true"></div>
    </div>
{/if}
